import React from 'react'

import {
  Alert,
  Box,
  Spinner,
  Text,
  WizardStep,
  WizardStepRenderParams,
  useOnNext,
} from '@factorialco/deprecated-design-system'
import { camelCase } from 'lodash'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useMemo } from 'modules/core/hooks/useMemo'
import { track } from 'modules/core/lib/Tracking'
import centsToDecimal from 'modules/core/lib/formatCents'
import { handleResult } from 'modules/core/lib/handleMutation'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import { EnumField } from 'modules/employees/components/UnifiedAddEmployeeModal/Fields/EnumField'
import { Fields } from 'modules/employees/components/UnifiedAddEmployeeModal/Fields/Fields'
import {
  WizardData,
  genericAmplitudeProperties,
} from 'modules/employees/components/UnifiedAddEmployeeModal/types'
import {
  getStringFieldValue,
  groupFieldsByGroup,
} from 'modules/employees/components/UnifiedAddEmployeeModal/utils'
import { useLegalEntityRoles } from 'modules/employees/hooks/useLegalEntityRoles'
import { unifiedAddEmployeeEvents } from 'modules/employees/lib/events'
import { useFinancialWorkspaceSeeAgreements } from 'modules/finance/components/FinancialWorkspace/hooks/useFinancialWorkspaceSeeAgreements'
import useCreateLevel from 'modules/job_catalog/hooks/useCreateLevel'
import { formatSalaryFromCents } from 'modules/job_catalog/lib/salaryUtils'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'

import LevelSelect from './LevelSelect'
import RolesSelect from './RolesSelect'
import { parseSalary } from './salaryUtils'
import useCreateRole from './useCreateRole'

const isCompletedStep = (data: WizardData) => {
  if (!data.contractsStartsOn) return false
  if (!data.workingHours && !!data.workingHoursFrequency) return false
  if (!!data.workingHours && !data.workingHoursFrequency) return false

  const isSalaryFieldValid = (!!data.salaryAmount && !!data.isSalaryValid) || !data.salaryAmount

  return isSalaryFieldValid
}

interface State {
  isOpenLevelSelector: boolean
  isOpenRoleSelector: boolean
  showRoleError: boolean
  workingHoursError: boolean
  workingHoursFrequencyError: boolean
}

const ContractInformationStep = ({ data, onDataChanged }: WizardStepRenderParams<WizardData>) => {
  const [state, setState]: [State, React.Dispatch<React.SetStateAction<State>>] =
    React.useState<State>({
      isOpenLevelSelector: false,
      isOpenRoleSelector: false,
      showRoleError: false,
      workingHoursError: false,
      workingHoursFrequencyError: false,
    })

  const canCreateJob = policyAuthorizations.for('job_catalog.create_job')
  const fields = data.fieldsSchema?.filter(
    (field) =>
      field.category === 'agreement_information' || field.category === 'payroll_information'
  )
  const spainFields = data.fieldsSchema?.filter(
    (field) => field.category === 'agreement_information_spain'
  )
  const [rolesCollection, isLoadingRoles, refetchRoles, canSeeJobCatalog] = useLegalEntityRoles(
    data.legalEntityId ? data.legalEntityId : data.legalEntity?.id
  )
  const filterFields = useMemo(() => {
    return fields?.filter((field) => {
      if (
        (field.fieldName === 'salary_amount' || field.fieldName === 'salary_frequency') &&
        !canSeeJobCatalog
      ) {
        return false
      }

      if (field.fieldName === 'trial_period_ends_on' && !data.hasTrialPeriod) {
        return false
      }

      if (
        !hasFeature('dev_time_tracking_workable_days_definition') &&
        (field.fieldName === 'contracts_bank_holiday_treatment' ||
          field.fieldName === 'contracts_working_time_percentage_in_cents' ||
          field.fieldName === 'contracts_max_legal_yearly_hours' ||
          field.fieldName === 'contracts_maximum_weekly_hours')
      ) {
        return false
      }

      return true
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSeeJobCatalog, data.hasTrialPeriod])
  const groupedFields = groupFieldsByGroup(filterFields)

  const {
    company: { id: companyId },
  } = useCurrentSession()

  const [createRole] = useCreateRole()
  const isRoleSelected = !!data.roleId
  const role = rolesCollection && data.roleId ? rolesCollection[data.roleId] : undefined
  const levels = role && rolesCollection ? rolesCollection[role.id]?.levels : undefined
  const isLevelSelected = data.jobLevelId
  const roleHasLevels = levels && levels.filter((level) => !level.isDefault).length > 0
  const createLevel = useCreateLevel()
  const level =
    role && data.jobLevelId
      ? role.levels.find((roleLevel) => roleLevel.id.toString() === data.jobLevelId)
      : undefined

  const salaryRangeFromLevel =
    level &&
    level.salaryRanges.find(
      (salaryRange) => salaryRange.salaryFrequency === data.workingHoursFrequency
    )

  const { salaryFrequency, minRange, maxRange, currency, amountType } = salaryRangeFromLevel || {}
  const containsSpainContractFields = data?.legalEntityCountry === 'es'
  const trialPeriodEndsOnRef = React.useRef<HTMLDivElement | null>(null)

  const getFieldError = (fieldName: string): string | undefined => {
    switch (fieldName) {
      case 'salaryAmount':
        return !data.isSalaryValid && !!data.salaryAmount
          ? i18n.t('add_employee_modal.contract_information_step.fields.salary_amount.error')
          : ''
      case 'workingHours':
        return state.workingHoursError
          ? i18n.t('add_employee_modal.contract_information_step.fields.hours.error')
          : ''
      case 'workingHoursFrequency':
        return state.workingHoursFrequencyError
          ? i18n.t('add_employee_modal.contract_information_step.fields.frequency.error')
          : ''
    }
  }

  useOnNext(() => {
    track(unifiedAddEmployeeEvents.addContractInformation, genericAmplitudeProperties(data))
  })

  const onCreateRole = async (name: string) => {
    if (data?.legalEntityId) {
      const result = await createRole(name, companyId, data.legalEntityId)
      const handled = handleResult(result, () => {
        setState({ ...state, showRoleError: true })
      })

      if (handled.success) {
        onDataChanged({ ...data, roleId: handled.data.role?.id?.toString() })
        refetchRoles()
      }
    }
  }

  const handleCreateLevel = async (name: string) => {
    if (role) {
      const result = await createLevel(name, role.id)
      const handled = handleResult(result, () => {
        //setState((state) => ({ ...state, errors['level']: errors }))
      })

      if (handled.success) {
        onDataChanged({
          ...data,
          jobLevelId: handled.data.level?.id?.toString(),
        })
        refetchRoles()
      }
    }
  }

  const minRangeUI = formatSalaryFromCents(minRange, data.legalEntityCountry, currency)
  const maxRangeUI = formatSalaryFromCents(maxRange, data.legalEntityCountry, currency)

  const salaryAlertMessage =
    amountType === 'range'
      ? i18n.t('employee_form.wizard.role_step.salary_range_alert', {
          min_range: minRangeUI,
          max_range: maxRangeUI,
          salary_frequency: salaryFrequency,
        })
      : i18n.t('employee_form.wizard.role_step.salary_fixed_alert', {
          fixed_amount: minRangeUI,
          salary_frequency: salaryFrequency,
        })

  const salaryOutOfRange = () => {
    const salaryAmount = parseSalary(data.salaryAmount)
    if (!salaryAmount || !minRange || !maxRange) return

    const minRangeDecimal = centsToDecimal(minRange)
    const maxRangeDecimal = centsToDecimal(maxRange)

    switch (amountType) {
      case 'range':
        return salaryAmount < minRangeDecimal || salaryAmount > maxRangeDecimal
      case 'fixed':
        return salaryAmount !== minRangeDecimal
      default:
        false
    }
  }

  const { loading } = useAsyncData(
    () => Promise.all([policyAuthorizations.authorizePolicies('job_catalog.create_job', [])]),
    []
  )

  useEffect(() => {
    if (data.hasTrialPeriod) {
      trialPeriodEndsOnRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [data.hasTrialPeriod])

  useEffect(() => {
    if (amountType === 'fixed' && minRange) {
      onSalaryChange(`${centsToDecimal(minRange)}`)
    } else {
      onSalaryChange('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amountType, minRange])

  useEffect(() => {
    if (isLoadingRoles) return

    if (isRoleSelected && !roleHasLevels) {
      onDataChanged({
        ...data,
        jobLevelId: levels?.find((roleLevel) => roleLevel.isDefault)?.id?.toString(),
      })
      return
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRoleSelected, roleHasLevels, isLoadingRoles])

  const onRoleSelect = (selectedRoleId: string | undefined) => {
    setState({ ...state, isOpenRoleSelector: false })
    onDataChanged({ ...data, roleId: selectedRoleId, jobLevelId: undefined })
  }

  const onLevelSelect = (levelId: string | undefined) => {
    onDataChanged({ ...data, jobLevelId: levelId })
    setState({ ...state, isOpenLevelSelector: false })
  }

  const onSalaryChange = (salary: string) => {
    const salaryAmount = parseSalary(salary)
    const isSalaryValid = !!salaryAmount && salaryAmount >= 0

    onDataChanged({ ...data, salaryAmount: salary, isSalaryValid })
  }

  const validateWorkingHours = (hours?: string, frequency?: string) => {
    if (!hours && !!frequency) {
      setState({
        ...state,
        workingHoursError: true,
      })
    }
    if (!frequency && !!hours) {
      setState({
        ...state,
        workingHoursFrequencyError: true,
      })
    }

    const hasHoursAndFrequency = !!hours && !!frequency
    const hasNoHoursAndFrequency = !hours && !frequency

    if (hasHoursAndFrequency || hasNoHoursAndFrequency) {
      setState({ ...state, workingHoursError: false, workingHoursFrequencyError: false })
    }
  }

  const onToggleRoleSelector = (isOpen: boolean) => {
    setState({ ...state, isOpenRoleSelector: isOpen, showRoleError: false })
  }

  const onToggleLevelSelector = (isOpen: boolean) => {
    setState({ ...state, isOpenLevelSelector: isOpen })
  }

  const onClickToggle = () => {
    onDataChanged({ ...data, hasTrialPeriod: !data.hasTrialPeriod })
  }

  const handleFieldValidation = (fieldName: string, value: string) => {
    if (fieldName === 'workingHours') {
      return validateWorkingHours(value, data.workingHoursFrequency)
    }
    if (fieldName === 'workingHoursFrequency') {
      return validateWorkingHours(data.workingHours, value)
    }
  }

  const onChangeField = (fieldName: string, value: string) => {
    onDataChanged({
      ...data,
      [fieldName]: value,
    })
    handleFieldValidation(fieldName, value)
  }

  const { canSeeFinanceWorkspace } = useFinancialWorkspaceSeeAgreements()

  if (loading || isLoadingRoles)
    return (
      <Box flexGrow justifyContent="center" alignItems="center">
        <Spinner />
      </Box>
    )

  return (
    <Box flexDirection="row" gap="s48">
      <Box flexDirection="column" gap="s24" width="s400">
        <Text.Header2>{i18n.t('add_employee_modal.contract_information_step.header')}</Text.Header2>
        {hasFeature('job_catalog') && canSeeJobCatalog && (
          <>
            <RolesSelect
              onRoleSelect={onRoleSelect}
              selectedRole={data.roleId}
              roles={Object.values(rolesCollection || {})}
              createJob={onCreateRole}
              onToggle={onToggleRoleSelector}
              error={
                state.showRoleError
                  ? i18n.t('employee_form.wizard.role_step.role_selector.error_no_level')
                  : undefined
              }
              isOpen={state.isOpenRoleSelector}
              canCreate={canCreateJob}
            />
            <LevelSelect
              onLevelSelect={onLevelSelect}
              selectedLevel={data.jobLevelId}
              levels={roleHasLevels ? levels : []}
              disabled={!isRoleSelected || (!roleHasLevels && !canCreateJob)}
              isOpen={state.isOpenLevelSelector}
              onToggle={onToggleLevelSelector}
              noLevels={isRoleSelected && !roleHasLevels}
              createLevel={handleCreateLevel}
              mandatory={isRoleSelected && !!roleHasLevels && !isLevelSelected}
              canCreate={canCreateJob}
            />
          </>
        )}
        {Object.entries(groupedFields ?? {}).map(([group, groupFields]) => (
          <Box key={group} flexDirection="row" gap="s16">
            {groupFields.map((field) => {
              return (
                <Fields
                  key={field.fieldName}
                  field={field}
                  data={data}
                  onChangeField={onChangeField}
                  getFieldError={getFieldError}
                  onMoneyChange={onSalaryChange}
                  onClickToggle={onClickToggle}
                  ref={trialPeriodEndsOnRef}
                />
              )
            })}
          </Box>
        ))}
        {containsSpainContractFields && (
          <>
            {spainFields?.map((field) => {
              // eslint-disable-next-line no-type-assertion/no-type-assertion
              const fieldName = camelCase(field.fieldName) as keyof WizardData
              return (
                <EnumField
                  key={field.fieldName}
                  dataSchemaItem={field}
                  value={getStringFieldValue(fieldName, data) || ''}
                  onChange={onChangeField}
                  mandatory={field.required}
                />
              )
            })}
          </>
        )}
      </Box>
      <Box flexDirection="column" paddingTop="s24" maxWidth="s240">
        <Box height="s200">
          <Box paddingTop="s16">
            {!isRoleSelected && !state.isOpenRoleSelector && (
              <Alert color="note">{i18n.t('employee_form.wizard.role_step.role_alert')}</Alert>
            )}
            {!isRoleSelected && state.isOpenRoleSelector && (
              <Alert color="info">{i18n.t('employee_form.wizard.role_step.add_role_alert')}</Alert>
            )}
          </Box>
          <Box paddingTop="s64">
            {isRoleSelected && isRoleSelected && !isLevelSelected && roleHasLevels && (
              <Alert color="warning">
                🚨 {i18n.t('employee_form.wizard.role_step.level_alert')}
              </Alert>
            )}
            {isRoleSelected && !isLevelSelected && !roleHasLevels && (
              <Alert color="info">{i18n.t('employee_form.wizard.role_step.add_level_alert')}</Alert>
            )}
            {isRoleSelected && !roleHasLevels && !canCreateJob && (
              <Alert color="note">{i18n.t('employee_form.wizard.role_step.not_level_alert')}</Alert>
            )}
          </Box>
        </Box>
        <Box paddingTop="s8" height="s140">
          {!data?.contractsStartsOn && (
            <Alert color="warning">
              {i18n.t('employee_form.wizard.contract_step.contract_start_on_warning')}
            </Alert>
          )}
        </Box>
        <Box paddingTop={'s96'}>
          {isLevelSelected && salaryRangeFromLevel && (
            <Alert color="warning">
              <Box flexDirection="column" gap="s4">
                {salaryOutOfRange() && (
                  <Text.Normal weight="bold">
                    {i18n.t('employee_form.wizard.role_step.salary.out_of_range')}
                  </Text.Normal>
                )}
                {amountType === 'fixed' && (
                  <Text.Normal weight="bold">
                    💡{i18n.t('employee_form.wizard.role_step.salary_fixed_alert_title')}
                  </Text.Normal>
                )}
                {salaryAlertMessage}
              </Box>
            </Alert>
          )}
        </Box>

        {canSeeFinanceWorkspace && (
          <Box paddingTop="s8" height="s140">
            <Alert color="info">
              {i18n.t('finance.financial_workspace.launch_info.agreements')}
            </Alert>
          </Box>
        )}
      </Box>
    </Box>
  )
}

export const contractInformationStep = (): WizardStep<WizardData> => ({
  title: i18n.t('add_employee_modal.contract_information_step.title'),
  nextText: i18n.t('common.next'),
  render: (props) => <ContractInformationStep {...props} />,
  isCompleted: isCompletedStep,
})
